import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useReadQuery } from '@apollo/client/react'
import { Badge, Button } from '@repo/shared-client/components/ui'
import { createFileRoute, Link, Outlet, useLocation } from '@tanstack/react-router'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import type { LikesProfileCountsQuery, LikesProfileCountsQueryVariables } from 'types/graphql'

export const LIKES_PROFILE_COUNTS_QUERY: TypedDocumentNode<LikesProfileCountsQuery, LikesProfileCountsQueryVariables> =
  gql`
  query LikesProfileCountsQuery($profileId: String!) {
    likesProfileCounts(filter: { profileId: $profileId }) {
      profileLikes
      profileLikesMe
    }
  }
`

export const Route = createFileRoute('/(auth)/(main-layout)/profile/$profileId/likes')({
  component: Layout,
  loader: async ({ context, params }) => {
    const { queryRef } = await queryAndPreload(context, LIKES_PROFILE_COUNTS_QUERY, {
      variables: { profileId: params.profileId },
    })
    return { queryRef }
  },
})

function Layout() {
  const { queryRef } = Route.useLoaderData()
  const { profileId } = Route.useParams()
  const { data } = useReadQuery(queryRef as QueryRef<LikesProfileCountsQuery>)
  const location = useLocation()
  const pathname = location.pathname

  const isMeActive = pathname.includes('/me')
  const isLikesActive = !isMeActive

  const likesProfileCounts = data.likesProfileCounts

  return (
    <>
      <div className="flex w-full flex-wrap items-center justify-center gap-2 py-2">
        <Button
          nativeButton={false}
          render={<Link className="no-underline" params={{ profileId } as never} to="/profile/$profileId/likes" />}
          size="sm"
          variant={isLikesActive ? 'pill' : 'pill-outline'}
        >
          Likes
          <Badge className="min-h-5 min-w-5 px-1" size="sm" variant="secondary">
            {likesProfileCounts?.profileLikes ?? 0}
          </Badge>
        </Button>
        <Button
          nativeButton={false}
          render={<Link className="no-underline" params={{ profileId } as never} to="/profile/$profileId/likes/me" />}
          size="sm"
          variant={isMeActive ? 'pill' : 'pill-outline'}
        >
          Likes Me
          {likesProfileCounts && likesProfileCounts.profileLikesMe > 0 && (
            <Badge className="min-h-5 min-w-5 px-1" size="sm" variant={isMeActive ? 'secondary' : 'default'}>
              {likesProfileCounts.profileLikesMe}
            </Badge>
          )}
        </Button>
      </div>
      <Outlet />
    </>
  )
}
